import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ApiClientError, apiFetch } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'

// ─── Types ───────────────────────────────────────────────────────────────────

interface IntelVenue {
  id: string
  name: string
  slug: string
  websiteUrl: string | null
  city: string | null
  country: string
  totalCapacity: number | null
  venueType: string | null
  overallConfidence: number
  activeStatus: boolean
}

interface IntelVenueSearchResponse {
  data: IntelVenue[]
  meta: { page: number; limit: number; total: number }
}

interface ImportedVenue {
  id: string
  name: string
  intelVenueId: string | null
}

interface Props {
  open: boolean
  onClose: () => void
  onImported: () => void
  /**
   * Set of intelVenueId values for venues the current artist has already
   * imported. Used to render the "Already imported" pill instead of the
   * Import button. Parent passes this in so we don't duplicate the venues
   * fetch inside the modal.
   */
  alreadyImportedIntelIds: Set<string>
}

// ─── Venue type options ──────────────────────────────────────────────────────

const VENUE_TYPES = [
  { value: '', label: 'Any type' },
  { value: 'theatre', label: 'Theatre' },
  { value: 'club', label: 'Club' },
  { value: 'arena', label: 'Arena' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'multi_purpose', label: 'Multi-purpose' },
  { value: 'other', label: 'Other' },
]

// ─── Confidence badge ────────────────────────────────────────────────────────

function ConfidenceBadge({ score }: { score: number }) {
  const color =
    score >= 80
      ? 'bg-green-100 text-green-800'
      : score >= 50
        ? 'bg-amber-100 text-amber-800'
        : 'bg-zinc-100 text-zinc-600'
  return (
    <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${color}`}>
      {score}
    </span>
  )
}

// ─── Debounce hook ───────────────────────────────────────────────────────────

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}

// ─── Main modal ──────────────────────────────────────────────────────────────

export function IntelVenueImportModal({
  open,
  onClose,
  onImported,
  alreadyImportedIntelIds,
}: Props) {
  const [city, setCity] = useState('')
  const [minCapacity, setMinCapacity] = useState('')
  const [maxCapacity, setMaxCapacity] = useState('')
  const [venueType, setVenueType] = useState('')
  const [country, setCountry] = useState('GB')

  // Track which intel venue ids were imported during this session so we
  // move them to the "Already imported" state without needing a refetch.
  const [sessionImported, setSessionImported] = useState<Set<string>>(new Set())
  const [lastImportedName, setLastImportedName] = useState<string | null>(null)
  const [importError, setImportError] = useState<string | null>(null)

  const debCity = useDebounced(city, 400)
  const debMin = useDebounced(minCapacity, 400)
  const debMax = useDebounced(maxCapacity, 400)
  const debType = useDebounced(venueType, 400)
  const debCountry = useDebounced(country, 400)

  const queryString = useMemo(() => {
    const p = new URLSearchParams()
    if (debCity) p.set('city', debCity)
    if (debMin) p.set('minCapacity', debMin)
    if (debMax) p.set('maxCapacity', debMax)
    if (debType) p.set('venueType', debType)
    if (debCountry) p.set('country', debCountry)
    p.set('page', '1')
    p.set('limit', '25')
    return p.toString()
  }, [debCity, debMin, debMax, debType, debCountry])

  // The intelligence endpoint is mounted ABOVE the tenant plugin (see
  // apps/api/src/modules/intelligence/intelligence.routes.ts header), so the
  // path is /intelligence/... not /artists/:artistId/intelligence/... — use
  // apiFetch directly rather than artistFetch.
  const {
    data: searchResult,
    isLoading: isSearching,
    error: searchError,
  } = useQuery<IntelVenueSearchResponse, ApiClientError>({
    queryKey: ['intel-venues-search', queryString],
    queryFn: () =>
      apiFetch<IntelVenueSearchResponse>(`/intelligence/venues?${queryString}`).then(
        // apiFetch unwraps the outer `data` envelope, but list endpoints
        // still return `{ data, meta }` at the wire. The envelope strip
        // gives us the inner array, not the full object — handle both
        // shapes defensively, as other pages in this codebase do.
        (raw) => {
          if (Array.isArray(raw)) {
            return { data: raw, meta: { page: 1, limit: 25, total: raw.length } }
          }
          return raw
        },
      ),
    enabled: open,
  })

  const importMutation = useMutation<
    ImportedVenue,
    ApiClientError,
    { intelVenueId: string; name: string }
  >({
    mutationFn: async ({ intelVenueId }) =>
      artistFetch<ImportedVenue>('/venues/import-from-intel', {
        method: 'POST',
        body: { intelVenueId },
      }),
    onSuccess: (venue, variables) => {
      setSessionImported((prev) => {
        const next = new Set(prev)
        next.add(variables.intelVenueId)
        return next
      })
      setLastImportedName(venue.name ?? variables.name)
      setImportError(null)
      onImported()
    },
    onError: (err) => {
      setImportError(err.message ?? 'Failed to import venue')
    },
  })

  if (!open) return null

  const results = searchResult?.data ?? []
  const total = searchResult?.meta?.total ?? 0

  const isImported = (intelId: string) =>
    alreadyImportedIntelIds.has(intelId) || sessionImported.has(intelId)

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="intel-import-title"
    >
      <div className="w-full max-w-3xl rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-ovation-ink/10 p-5">
          <div>
            <h3 id="intel-import-title" className="font-display text-xl">
              Import from Venue Intelligence
            </h3>
            <p className="mt-1 text-sm text-ovation-muted">
              Browse canonical venue records and add them to your venue list.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-ovation-muted hover:bg-ovation-ink/5 hover:text-ovation-ink"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 gap-3 border-b border-ovation-ink/10 p-5 sm:grid-cols-5">
          <div className="col-span-2 sm:col-span-1">
            <label className="mb-1 block text-xs text-ovation-muted">City</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/20 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
              placeholder="e.g. London"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-ovation-muted">Min capacity</label>
            <input
              type="number"
              value={minCapacity}
              onChange={(e) => setMinCapacity(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/20 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
              placeholder="0"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-ovation-muted">Max capacity</label>
            <input
              type="number"
              value={maxCapacity}
              onChange={(e) => setMaxCapacity(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/20 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
              placeholder="any"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-ovation-muted">Type</label>
            <select
              value={venueType}
              onChange={(e) => setVenueType(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/20 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
            >
              {VENUE_TYPES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-ovation-muted">Country</label>
            <input
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
              maxLength={2}
              className="w-full rounded-md border border-ovation-ink/20 px-3 py-2 text-sm uppercase focus:border-ovation-ink focus:outline-none"
              placeholder="GB"
            />
          </div>
        </div>

        {/* Flash banners */}
        {(lastImportedName || importError) && (
          <div className="space-y-2 px-5 pt-4">
            {lastImportedName && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                Imported: {lastImportedName}
              </div>
            )}
            {importError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {importError}
              </div>
            )}
          </div>
        )}

        {/* Results */}
        <div className="max-h-[55vh] overflow-y-auto p-5">
          {isSearching ? (
            <p className="text-sm text-ovation-muted">Loading…</p>
          ) : searchError ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {searchError.message}
            </div>
          ) : results.length === 0 ? (
            <p className="text-sm text-ovation-muted">No matching venues</p>
          ) : (
            <>
              <p className="mb-3 text-xs text-ovation-muted">
                {total} venue{total === 1 ? '' : 's'} found
              </p>
              <ul className="space-y-2">
                {results.map((v) => {
                  const imported = isImported(v.id)
                  const pending =
                    importMutation.isPending &&
                    importMutation.variables?.intelVenueId === v.id
                  return (
                    <li
                      key={v.id}
                      className="flex items-start justify-between gap-3 rounded-md border border-ovation-ink/10 p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-semibold">{v.name}</span>
                          {v.venueType && (
                            <span className="rounded bg-ovation-ink/5 px-1.5 py-0.5 text-xs capitalize text-ovation-muted">
                              {v.venueType.replace(/_/g, ' ')}
                            </span>
                          )}
                          <ConfidenceBadge score={v.overallConfidence} />
                        </div>
                        <div className="mt-0.5 text-sm text-ovation-muted">
                          {v.city ?? 'Unknown city'}, {v.country} ·{' '}
                          {v.totalCapacity != null
                            ? `${v.totalCapacity.toLocaleString()} cap`
                            : '—'}
                        </div>
                      </div>
                      <div className="shrink-0">
                        {imported ? (
                          <span className="inline-block cursor-not-allowed rounded-md border border-ovation-ink/10 bg-ovation-ink/5 px-3 py-1.5 text-xs text-ovation-muted">
                            Already imported
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              importMutation.mutate({
                                intelVenueId: v.id,
                                name: v.name,
                              })
                            }
                            disabled={pending}
                            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
                          >
                            {pending ? 'Importing…' : 'Import'}
                          </button>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-ovation-ink/10 p-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ovation-ink/20 px-4 py-2 text-sm hover:bg-ovation-ink/5"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
